import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faXmark,
  faArrowDownWideShort,
  faSliders,
} from '@fortawesome/free-solid-svg-icons';
import { customerJobService } from '@/services/customer-job.service';
import { customerJobDetailPath } from '@/routes/customer-portal-routes';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { locationParamFor, useCustomerScopeStore } from '@/stores/customer-scope.store';
import { CustomerPageHeader } from '@/components/customer-portal/CustomerPageHeader';
import { CustomerSearchBar } from '@/components/customer-portal/CustomerSearchBar';
import { CustomerPagination } from '@/components/customer-portal/CustomerPagination';
import { CustomerEmptyState } from '@/components/customer-portal/CustomerEmptyState';
import { CustomerErrorState } from '@/components/customer-portal/CustomerErrorState';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import { CustomerLocationContext } from '@/components/customer-portal/CustomerLocationContext';
import { CustomerSkeletonList } from '@/components/customer-portal/skeletons/CustomerSkeleton';
import { useDebounce } from '@/hooks/use-debounce';
import { CustomerDecisionCard } from './CustomerDecisionCard';
import {
  DECISION_PAGE_SIZES,
  DECISION_SORTS,
  DEFAULT_DECISION_PAGE_SIZE,
  DEFAULT_DECISION_SORT,
  parseSort,
  type DecisionSortValue,
} from './customer-decision-queue';

/**
 * D-130 Phase 6C — the full decision queue.
 *
 * Every row here is a job the SERVER returned for
 * `GET /customer-portal/jobs?actionRequired=true`, already location-scoped.
 * Search, location, sort and pagination are all server parameters: nothing on
 * this page filters, re-orders or hides a record in the browser.
 *
 * Filters this page deliberately does NOT offer (Phase 6A gaps G1/G3): request
 * type, priority and waiting-age. The list endpoint returns neither priority
 * nor service type and accepts no such filter, so offering them would either
 * fabricate data or filter a single page client-side and lie about the totals.
 * Waiting age is DISPLAYED per row (derived from `updatedAt`) but is not a
 * filter, and sorting by "waiting longest" is the server's `updatedAt asc`.
 */
export function CustomerDecisionQueuePage() {
  const navigate = useNavigate();
  const user = useCustomerAuthStore((s) => s.user);
  const selectedLocation = useCustomerScopeStore((s) => s.selectedLocationId);
  const setSelectedLocation = useCustomerScopeStore((s) => s.setSelectedLocation);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_DECISION_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<DecisionSortValue>(DEFAULT_DECISION_SORT);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 350);

  const locationId = locationParamFor(user?.locationScope, selectedLocation);
  const { sortBy, sortDir } = parseSort(sort);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: [
      'customer-portal',
      'decisions',
      page,
      pageSize,
      debouncedSearch,
      sort,
      locationId ?? 'all',
    ],
    queryFn: () =>
      customerJobService.list({
        page,
        pageSize,
        actionRequired: true,
        search: debouncedSearch || undefined,
        sortBy: sortBy as 'inspectionNumber' | 'receivedAt' | 'updatedAt',
        sortDir,
        locationId,
      }),
    placeholderData: keepPreviousData,
  });

  const isOrg = user?.locationScope === 'ORGANIZATION';
  const locationChipLabel =
    selectedLocation === 'unassigned'
      ? 'Unassigned'
      : (user?.locations?.find((l) => l.id === selectedLocation)?.name ?? null);
  const sortLabel = DECISION_SORTS.find((s) => s.value === sort)?.label ?? '';
  const hasFilters =
    Boolean(debouncedSearch) || sort !== DEFAULT_DECISION_SORT || Boolean(locationId);

  const clearAll = () => {
    setSearch('');
    setSort(DEFAULT_DECISION_SORT);
    if (isOrg) setSelectedLocation(null);
    setPage(1);
  };

  const total = data?.total ?? 0;

  return (
    <div className="space-y-4 sm:space-y-5">
      <CustomerPageHeader
        title="Decisions"
        subtitle="Jobs that are waiting on your response"
        eyebrow={data ? `${total} waiting on you` : undefined}
      />

      {/* ── Controls. Stacked on phones; the sort/page-size pair collapses into
             a sheet so 320px keeps only search + location on the first row. ── */}
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <CustomerSearchBar
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search job number, asset or serial…"
          />
          <div className="flex items-center gap-2">
            <CustomerLocationContext className="min-w-0 flex-1 sm:w-52 sm:flex-none" />
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              aria-controls="decision-sort-panel"
              className="customer-pressable inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-customer-sm border border-customer-border bg-customer-surface px-3 text-sm font-medium text-customer-text transition-colors hover:border-customer-action/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
            >
              <FontAwesomeIcon icon={faSliders} className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Sort</span>
            </button>
          </div>
        </div>

        {filtersOpen && (
          <div
            id="decision-sort-panel"
            className="customer-slide-up grid gap-3 rounded-customer-lg border border-customer-border bg-customer-surface p-3 sm:grid-cols-2"
          >
            <label className="min-w-0 text-xs font-medium text-customer-text-secondary">
              <span className="mb-1 flex items-center gap-1.5">
                <FontAwesomeIcon
                  icon={faArrowDownWideShort}
                  className="h-3 w-3"
                  aria-hidden="true"
                />
                Sort by
              </span>
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value as DecisionSortValue);
                  setPage(1);
                }}
                className="min-h-11 w-full rounded-customer-sm border border-customer-border bg-customer-surface px-2.5 text-sm text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
              >
                {DECISION_SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-0 text-xs font-medium text-customer-text-secondary">
              <span className="mb-1 block">Per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="min-h-11 w-full rounded-customer-sm border border-customer-border bg-customer-surface px-2.5 text-sm text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
              >
                {DECISION_PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n} per page
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {/* Active filter chips — what is narrowing the list right now. */}
        {hasFilters && (
          <div className="flex flex-wrap items-center gap-1.5">
            {debouncedSearch && (
              <Chip label={`Search: ${debouncedSearch}`} onClear={() => setSearch('')} />
            )}
            {locationId && (
              <Chip
                label={`Location: ${locationChipLabel ?? locationId}`}
                onClear={() => setSelectedLocation(null)}
              />
            )}
            {sort !== DEFAULT_DECISION_SORT && (
              <Chip label={sortLabel} onClear={() => setSort(DEFAULT_DECISION_SORT)} />
            )}
            <button
              type="button"
              onClick={clearAll}
              className="customer-pressable min-h-11 rounded-customer-sm px-2 text-xs font-semibold text-customer-action-text underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {isLoading && <CustomerSkeletonList rows={6} />}

      {isError && !isLoading && (
        <CustomerErrorState
          message="We couldn't load your decisions."
          onRetry={() => void refetch()}
        />
      )}

      {data && !isLoading && !isError && (
        <>
          {data.items.length === 0 ? (
            <div className="rounded-customer border border-dashed border-customer-border bg-customer-surface">
              <CustomerEmptyState
                icon={faCircleCheck}
                tone="neutral"
                title={hasFilters ? 'No decisions match' : "You're all caught up"}
                description={
                  hasFilters
                    ? 'No decisions match your current search or filters.'
                    : 'Nothing is waiting on your response right now.'
                }
                className="py-14"
              />
              {hasFilters && (
                <div className="flex justify-center pb-6">
                  <CustomerButton variant="outline" onClick={clearAll}>
                    Clear filters
                  </CustomerButton>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`customer-fade-in overflow-hidden rounded-customer border border-customer-border bg-customer-surface shadow-customer-sm ${
                isFetching ? 'opacity-70 transition-opacity' : ''
              }`}
            >
              <ul className="divide-y divide-customer-border">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <CustomerDecisionCard
                      item={item}
                      onOpen={(id) => navigate(customerJobDetailPath(id))}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <CustomerPagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

/**
 * An active filter, and the control that removes it. The WHOLE chip is the
 * button: a 24px "×" would be a comfortable click and an uncomfortable tap, and
 * splitting label from control buys nothing here. `min-h-11` keeps the target at
 * the 44px touch minimum at every width.
 */
function Chip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button
      type="button"
      onClick={onClear}
      aria-label={`Remove filter: ${label}`}
      className="customer-pressable inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-full border border-customer-border bg-customer-surface-raised px-3 text-xs font-medium text-customer-text-secondary transition-colors hover:border-customer-action/40 hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring"
    >
      <span className="truncate">{label}</span>
      <FontAwesomeIcon icon={faXmark} className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
    </button>
  );
}
